import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { J } from '~/common/constants/keyCodes';
import QuickSearchModal from '~/components/Shared/QuickSearchModal/QuickSearchModal';
import { useDisableBodyScroll } from '~/eds/src/hooks/useDisableBodyScroll';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';
import search from '~/redux/slices/search';
import { useRouting } from '~/routing/useRouting';
import { MODAL_QUICK_SEARCH } from '~/types/modal.types';

export const useSearchModal = (): [React.ReactNode, () => void] => {
  const hasSearchV3 = useFlag(FlagType.SearchV3);
  const { location, navigate } = useRouting();
  const [modal, setModal] = useState<string | null>(null);
  useDisableBodyScroll(modal === MODAL_QUICK_SEARCH);

  const { pathname } = location;

  const handleShowModal = () => {
    setModal(MODAL_QUICK_SEARCH);
  };

  const handleHideModal = () => {
    setModal(null);
  };

  const handleOnSearchShortcutPress = (event: KeyboardEvent) => {
    if (event.keyCode === J && event.metaKey) {
      handleShowModal();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleOnSearchShortcutPress, false);

    return () => {
      document.removeEventListener(
        'keydown',
        handleOnSearchShortcutPress,
        false,
      );
    };
  }, []);

  const dispatch = useDispatch();

  const navigateTo = (path: string) => {
    if (pathname !== path) navigate(path);
  };

  const searchOnSearchV3 = (query: string) => {
    dispatch(slices.searchV3.actions.reset());
    dispatch(slices.searchV3.actions.setSearchText(query));
  };

  const goToSearchPage = (query: string) => {
    if (hasSearchV3) {
      searchOnSearchV3(query);
    } else {
      dispatch(search.actions.clearUnsupportedQuery());
      dispatch(search.actions.setBooleanQuery(query));
      dispatch(
        search.actions.setQuery({
          booleanQuery: query,
          filters: [],
          fields: {},
        }),
      );
      dispatch(search.actions.clearFilterValues());
    }
    navigateTo('/search');
  };

  const renderSearchOverlay = () => {
    return (
      <QuickSearchModal
        isVisible={modal === MODAL_QUICK_SEARCH}
        hideModal={handleHideModal}
        onSearchSubmit={goToSearchPage}
      />
    );
  };

  const searchModal = (
    <>{modal === MODAL_QUICK_SEARCH && renderSearchOverlay()}</>
  );

  return [searchModal, handleShowModal];
};
